/**
 *    ABOUT CORP CONTROLLER
 *    handles About Menu items: About / Contact / Verity Terms / Client Terms
 */
import React, { useState, useEffect, useRef, MouseEvent } from 'react';
import { useCurrentStateAndParams, useRouter } from '@uirouter/react';
import { ngDelegate, ngContext } from '../../core/services/ng.delegate';
import { UserInfo } from '../../../global';
import validSvc from '../../core/validSvc';
import termsRepo from '../../core/repo/termsRepo'; // '../../ /core/repo/termsRepo';
import { CorpAboutTab, CorpContactTab } from './ui/corpTabs';
import PageTop from '../../core/components/PageTop';

enum Render {
    INIT = 'INIT',
    ABOUT = 'ABOUT',
    TERMS = 'TERMS',
    UTERMS = 'UTERMS',
    CONTACT = 'CONTACT',
}

type Store = {
    pendingTab: Render;
    User: UserInfo | null;
    verityTerms: string;
    userTerms: string;
    userTermsTitle: string;
};

const AboutCorpCtl = (): JSX.Element => {
    const router = useRouter();
    const { params: routeParams } = useCurrentStateAndParams();

    const _store = useRef<Store>({
        pendingTab: Render.INIT,
        User: null,
        verityTerms: '',
        userTerms: '',
        userTermsTitle: '',
    });

    // render
    const [_render, setRender] = useState<Render>(Render.INIT);

    // INIT
    useEffect(() => {
        const store = _store.current;
        store.User = ngContext().User;

        // param:tab
        let defaultTab = Render.ABOUT;
        if (!!routeParams && validSvc.isText(routeParams.tab)) {
            defaultTab = routeParams.tab as Render;
        }

        // get terms for logged in user
        if (!!store.User) {
            store.pendingTab = defaultTab;
            initTerms(); // ASYNC
            return;
        }

        // user is not logged in show default
        setRender(defaultTab);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const initTerms = async (): Promise<void> => {
        try {
            // verity terms
            let r: any = await termsRepo.get(true);
            const store = _store.current;
            store.verityTerms = r.Content;

            // user terms
            const usr = store.User || ({} as UserInfo);
            if (!isVerity(usr.Type)) {
                r = await termsRepo.get(false);
                _store.current.userTerms = r.Content;
                _store.current.userTermsTitle = usr.Company.toUpperCase() + ' TERMS OF USE';
            }

            setRender(store.pendingTab);
        } catch (err: any) {
            if (!!err) {
                ngDelegate.ok('Terms Not Available', JSON.stringify(err));
            }
            _store.current.verityTerms = '';
            _store.current.userTerms = '';
            setRender(Render.ABOUT);
        }
    };

    function tabChange(e: MouseEvent, newTab: Render): void {
        e.preventDefault();
        setRender(newTab);
    }

    function gotoLogin(e: MouseEvent): void {
        e.preventDefault();
        router.stateService.go('login');
    }

    // RENDER

    const TabItem = ({ tabId, title }: { tabId: Render; title: string }): JSX.Element => {
        const hasFocus = _render == tabId ? 'has-focus' : '';
        return (
            <li
                className={hasFocus}
                onClick={e => tabChange(e, tabId)}
            >
                {title}
            </li>
        );
    };
    const TabBar = (): JSX.Element | null => {
        if (_render == Render.INIT) {
            return null;
        }

        const store = _store.current;
        const hasUser = !!store.User;
        const hasVerityTerms = hasUser && !!store.verityTerms;
        const hasClientTerms = hasUser && !isVerity(store.User?.Type) && !!store.userTerms;

        return (
            <section className="tab-bar">
                <ul>
                    {hasVerityTerms && (
                        <TabItem
                            tabId={Render.TERMS}
                            title={'VERITY TERMS OF USE & PRIVACY POLICY'}
                        />
                    )}
                    {hasClientTerms && (
                        <TabItem
                            tabId={Render.UTERMS}
                            title={store.userTermsTitle}
                        />
                    )}
                    <TabItem
                        tabId={Render.ABOUT}
                        title={'ABOUT VERITY'}
                    />
                    <TabItem
                        tabId={Render.CONTACT}
                        title={'CONTACT US'}
                    />
                    {!hasUser && (
                        <li className="tab-move-right">
                            <a onClick={e => gotoLogin(e)}>
                                <span className="icon-glyph icon-security"></span>&nbsp;&nbsp;Return to Login Page...
                            </a>
                        </li>
                    )}
                </ul>
            </section>
        );
    };

    const Terms = ({ tabId }: { tabId: Render }): JSX.Element => {
        const store = _store.current;
        if (tabId == Render.TERMS) {
            return (
                <div
                    className="tab-panel about-tou"
                    dangerouslySetInnerHTML={{ __html: store.verityTerms }}
                ></div>
            );
        } else {
            return (
                <div
                    className="tab-panel about-tou"
                    dangerouslySetInnerHTML={{ __html: store.userTerms }}
                ></div>
            );
        }
    };

    const TabPanel = (): JSX.Element | null => {
        switch (_render) {
            case Render.ABOUT:
                return <CorpAboutTab />;
            case Render.CONTACT:
                return <CorpContactTab />;
            case Render.TERMS:
            case Render.UTERMS:
                return <Terms tabId={_render} />;
            default:
                return null;
        }
    };

    // RENDER MAIN
    return (
        <>
            <PageTop
                title="Verity Solutions"
                icon="icon-about-corp"
            />
            <div className="corp-feature">
                <TabBar />
                <TabPanel />
            </div>
        </>
    );

    // HELPERS
    // function checkTerms(content) {
    //     // only <p and <h tags supported
    //     const regexTags = /(<)(?!\/)(?![ph])(?![PH])/;
    //     if (regexTags.test(content)) {
    //         return false;
    //     } else {
    //         return true;
    //     }
    // }

    function isVerity(utype): boolean {
        return utype == 'VERITY' || utype == 'VERITYSA';
    }
};

export default AboutCorpCtl;
